import { createSelector } from '@reduxjs/toolkit';
import { get } from 'lodash';
import Joi from 'joi';
import { OrderSliceSelectors } from '../../slice';
import { FORM, SHIPMENT } from '../../../../constants';
import { parcelSchema } from '../../validators/parcel';
import {
  NetworkHelpers,
  ShipmentHelpers,
  Validators,
} from '../../../../helpers';
import { countriesSelectors } from '../../../../redux/countriesSlice';
import { validateParcelDimensionsForShop } from '../../../../helpers/shipment';
import { getAvailableNetworks } from './helpers';

export const getOutboundNetworkParameters = createSelector(
  OrderSliceSelectors.getValues,
  NetworkHelpers.getNetworkQuery
);

export const getNetworksByParcelParams = createSelector(
  [OrderSliceSelectors.getReferences, OrderSliceSelectors.getValues],
  ({ networks }, values) =>
    NetworkHelpers.getNetworksByParcelSizeAndWeight(networks, values)
);

export const getFilteredNetworks = createSelector(
  [getNetworksByParcelParams, OrderSliceSelectors.getValues],
  getAvailableNetworks
);

export const getMaxWeightAndNumberOfParcels = createSelector(
  [OrderSliceSelectors.getReferences],
  references => NetworkHelpers.getMaxParcelDimensions(references.networks)
);

export const validateParcelsValues = createSelector(
  [OrderSliceSelectors.getValues, getMaxWeightAndNumberOfParcels],
  (values, context) => {
    const parcels = get(values, FORM.SHIPMENT_FIELDS.PARCELS.KEY, []);
    // eslint-disable-next-line no-unused-vars
    const parcelsWithoutProducts = parcels.map(({ products, ...rest }) => rest);

    const { error } = Joi.array()
      .items(
        Validators.requireKeysSchema(parcelSchema(context), [
          '_weight',
          '_quantity',
          '_value',
          '_width',
          '_height',
          '_length',
        ])
      )
      .min(1)
      .validate(parcelsWithoutProducts);

    return error;
  }
);

export const getParcelDetailsRequiredFields = createSelector(
  [
    OrderSliceSelectors.getValues,
    OrderSliceSelectors.getReferences,
    countriesSelectors.getCountries,
  ],
  ShipmentHelpers.getParcelDetailsRequiredFields
);

export const getParcelDetailsRequiredFieldsMap = createSelector(
  [getParcelDetailsRequiredFields],
  requiredFields =>
    requiredFields.reduce((acc, item) => {
      acc[item] = true;
      return acc;
    }, {})
);

export const getDropAtShopAvailable = createSelector(
  [getNetworksByParcelParams, OrderSliceSelectors.getValues],
  (networks, values) => {
    const parcels = get(values, FORM.SHIPMENT_FIELDS.PARCELS.KEY, []);

    return validateParcelDimensionsForShop(parcels);
  }
);

export const getShipToShopAvailable = createSelector(
  [getNetworksByParcelParams, OrderSliceSelectors.getValues],
  NetworkHelpers.getShipToShopAvailable
);
